import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, UploadCloud, X, AlertCircle, Square } from "lucide-react";
import { appsApi, errMsg, notifyError, inputCls, btnDark, fmtSize } from "@/lib/appsApi";
import { uploadInChunks } from "@/lib/chunkUpload";
import { readApkInfo } from "@/lib/apkInfo";
import { readBundleContents, findMissing } from "@/lib/bundleInfo";
import { readAeropack, aeropackMeta, aeropackInfoFields } from "@/lib/aeropack";
import { PackageDetails, UnitPicker, matchUnit } from "@/components/bundles/PackageTargets";
import { BundleContents } from "@/components/apps/BundleContents";
import { FileTypesEditor } from "@/components/apps/FileTypesEditor";

const today = () => new Date().toLocaleDateString("en-CA");
const MAX = 900 * 1024 * 1024;
const label = "mb-1 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500";
// Accepts a bare hash or a pasted line like "<hash>  bundle.zip" / "SHA256 (bundle.zip) = <hash>".
const pickSha = (s) => (/\b[0-9a-f]{64}\b/i.exec(s || "")?.[0] ?? "").toLowerCase();
const emptyMeta = (fileType = "APK") => ({ description: "", version: "", fileType, fileDate: today(), sha: "" });

const fmtEta = (s) => {
  if (s == null) return "working out time left…";
  if (s < 60) return `about ${Math.max(1, Math.round(s))} s left`;
  const m = Math.floor(s / 60);
  if (m < 60) return `about ${m} min ${Math.round(s % 60)} s left`;
  return `about ${Math.floor(m / 60)} h ${m % 60} min left`;
};

const UploadStats = ({ progress, stats }) => (
  <span className="min-w-0 max-w-full text-xs text-slate-600" data-testid="apps-upload-progress">
    Uploading {progress}%
    {stats && (
      <span className="mt-1 flex flex-wrap justify-center gap-x-2 font-mono text-[11px] text-slate-600" data-testid="apps-upload-stats">
        <span data-testid="apps-upload-sent">{fmtSize(stats.sent)} of {fmtSize(stats.total)}</span>
        <span data-testid="apps-upload-speed">· {stats.speed != null ? `${fmtSize(stats.speed)}/s` : "—/s"}</span>
        <span data-testid="apps-upload-eta">· {fmtEta(stats.eta)}</span>
      </span>
    )}
  </span>
);

export const UploadForm = ({ onUploaded, existing = [], requiredPatterns = [], tabs = [], activeTab = "", fileTypes = [], onTypesSaved, pkg = null }) => {
  const [editTypes, setEditTypes] = useState(false);
  const [tabId, setTabId] = useState(activeTab);
  useEffect(() => { setTabId(activeTab); }, [activeTab]);
  const [file, setFile] = useState(null);
  const [apk, setApk] = useState(null);
  const [apkNote, setApkNote] = useState("");
  const [bundleBuild, setBundleBuild] = useState(null);
  const [bundle, setBundle] = useState({ entries: null, dirs: null, loading: false });
  const [uploadId, setUploadId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(() => emptyMeta(pkg ? "Data package" : "APK"));
  const [pack, setPack] = useState(null);
  const [unitIds, setUnitIds] = useState([]);
  const [newUnit, setNewUnit] = useState(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const serverIdRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const fail = (msg) => {
    setError(msg);
    notifyError(msg);
  };

  // Tell the server to throw away parts already received for an upload that won't be saved.
  const discardOnServer = () => {
    const id = serverIdRef.current;
    serverIdRef.current = null;
    if (id) appsApi.delete(`/uploads/${id}`).catch(() => {});
  };

  const stopUpload = () => {
    discardOnServer();
    reset();
    toast.success("Upload stopped. Nothing was saved.");
  };

  const reset = () => {
    abortRef.current?.abort();
    setFile(null);
    setUploadId(null);
    setApk(null);
    setBundleBuild(null);
    setBundle({ entries: null, loading: false });
    setApkNote("");
    setPack(null);
    setUnitIds([]);
    setNewUnit(null);
    setProgress(0);
    setStats(null);
    setPhase("idle");
    if (inputRef.current) inputRef.current.value = "";
  };

  const pick = async (f) => {
    discardOnServer();
    reset();
    setError("");
    if (!f) return;
    if (f.size === 0) return fail(`"${f.name}" is empty (0 bytes), so there's nothing to upload.`);
    if (f.size > MAX) return fail(`"${f.name}" is ${fmtSize(f.size)}, which is over the 900 MB limit.`);
    const ext = (f.name.includes(".") ? f.name.split(".").pop() : "").toLowerCase();
    const t = pkg ? null : fileTypes.find((x) => x.ext && x.ext === ext)?.name;
    if (t) setMeta((m) => ({ ...m, fileType: t }));
    if (ext === "zip" && !pkg) {
      const sig = new Uint8Array(await f.slice(0, 2).arrayBuffer());
      if (sig[0] !== 0x50 || sig[1] !== 0x4b) return fail(`"${f.name}" ends in .zip but isn't a valid ZIP archive. Re-create the Mission Ready Bundle and try again.`);
      const build = /(\d+)\D*$/.exec(f.name.replace(/\.zip$/i, ""))?.[1];
      setBundleBuild(build ? Number(build) : null);
      if (build) setMeta((m) => ({ ...m, version: build }));
      else setApkNote(`No build number found at the end of "${f.name}", so enter the version yourself.`);
      setBundle({ entries: null, loading: true });
      readBundleContents(f)
        .then((info) => setBundle({ ...info, loading: false }))
        .catch((err) => {
          setBundle({ entries: null, loading: false });
          setApkNote(`Couldn't list the files inside this bundle, so check its contents yourself before saving. Reason: ${err?.message || "unknown"}`);
        });
    }
    setFile(f);
    if (pkg) readPackage(f);
    if (ext === "apk") {
      readApkInfo(f)
        .then((info) => {
          setApk(info);
          if (info.version_name) setMeta((m) => ({ ...m, version: info.version_name }));
          const dup = existing.find((x) => x.package_name === info.package_name && info.version_code != null && x.version_code === info.version_code);
          if (dup) setApkNote(`Heads up: ${info.package_name} version code ${info.version_code} is already on the list (uploaded ${dup.file_date || dup.created_at?.slice(0, 10)}). Android won't install it over that build unless the version code is higher.`);
        })
        .catch((err) => setApkNote(`Couldn't read the version details from this APK, so fill them in yourself. Reason: ${err?.message || "unknown"}`));
    }
    setPhase("uploading");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const id = await uploadInChunks(f, (pct, s) => { setProgress(pct); setStats(s ?? null); }, ctrl.signal, (sid) => { serverIdRef.current = sid; });
      setUploadId(id);
      setPhase("uploaded");
    } catch (err) {
      if (ctrl.signal.aborted) return undefined;
      setPhase("idle");
      fail(err?.message || "The upload stopped for an unknown reason.");
    }
    return undefined;
  };

  // Mission Ready .aeropack: fill in the details from info.json, pick the unit(s), then check every file's checksum.
  const readPackage = async (f) => {
    const sig = new Uint8Array(await f.slice(0, 2).arrayBuffer());
    if (sig[0] !== 0x50 || sig[1] !== 0x4b) return undefined;
    let p = null;
    try {
      p = await readAeropack(f);
    } catch (err) {
      setApkNote(`Couldn't read the package details, so fill them in yourself. Reason: ${err?.message || "unknown"}`);
      return undefined;
    }
    if (!p) return undefined;
    const { info } = p;
    setMeta((m) => ({ ...m, ...Object.fromEntries(Object.entries(aeropackMeta(info)).filter(([, v]) => v)) }));
    if (info.app && info.app.toLowerCase() !== pkg.appName.toLowerCase()) setApkNote(`Heads up: this package was made by ${info.app}, not ${pkg.appName}.`);
    if (info.package_type === "deployment") {
      const u = matchUnit(pkg.units, info);
      if (u) setUnitIds([u.id]);
      else if (info.destination?.name) setNewUnit({ name: info.destination.name, group_id: info.destination.id || "" });
    }
    setPack({ info, check: { state: "checking", done: 0, total: 0 } });
    try {
      const result = await p.verify((done, total) => setPack((s) => (s?.info === info ? { ...s, check: { state: "checking", done, total } } : s)));
      setPack((s) => (s?.info === info ? { ...s, check: { state: "done", result } } : s));
    } catch (err) {
      setPack((s) => (s?.info === info ? { ...s, check: { state: "done", result: null, error: err?.message || "unknown" } } : s));
    }
    return undefined;
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (phase !== "uploaded" || !uploadId) return fail("Step 1 isn't finished yet: choose a file and wait for the upload to reach 100% before saving.");
    if (pkg && !newUnit && !unitIds.some((id) => pkg.units.some((u) => u.id === id))) return fail("Choose which unit(s) this package is for. Add a unit first if there are none.");
    if (!pkg && !tabs.some((t) => t.id === tabId)) return fail("Choose which tab this file goes in.");
    if (meta.sha.trim() && !pickSha(meta.sha)) return fail("The SHA-256 checksum should be 64 letters and numbers (0–9, a–f). Paste it again from the build job, or leave the box empty.");
    setPhase("saving");
    let targets = unitIds;
    if (pkg && newUnit) {
      const id = await pkg.addUnit(newUnit);
      if (!id) {
        setPhase("uploaded");
        return undefined;
      }
      targets = [id];
      setNewUnit(null);
      setUnitIds(targets);
    }
    const info = pack?.info;
    try {
      const r = await appsApi.post(`/uploads/${uploadId}/complete`, {
        description: meta.description, version: meta.version, file_type: meta.fileType, file_date: meta.fileDate,
        package_name: apk?.package_name ?? "", version_code: apk?.version_code ?? bundleBuild ?? null, min_sdk: apk?.min_sdk ?? "", target_sdk: apk?.target_sdk ?? "", tab_id: pkg ? pkg.tabId : tabId,
        ...(pkg ? {
          bundle_app: pkg.appId, unit_ids: targets, package_type: info?.package_type ?? "", min_build: info?.min_build ?? null,
          contents_verified: pack?.check?.state === "done" && !pack.check.error ? Boolean(pack.check.result?.ok) : null,
          package_info: info ? aeropackInfoFields(info) : {},
        } : {}),
        expected_sha256: pickSha(meta.sha),
      });
      serverIdRef.current = null;
      onUploaded(r?.data);
      toast.success("Saved — the link appears below once storage finishes. We'll email you when it's done, so you can leave this page.");
      reset();
      setMeta(emptyMeta(pkg ? "Data package" : "APK"));
    } catch (err) {
      setPhase("uploaded");
      fail(errMsg(err, "Saving the file details"));
    }
    return undefined;
  };

  return (
    <form onSubmit={save} className="min-w-0 rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8" data-testid="apps-upload-form">
      <h3 className="font-display text-xl font-semibold text-ink">{pkg ? `Upload a ${pkg.appName} package` : "Upload a file"}</h3>
      <p className={`${label} mt-5`}>Step 1 · Upload</p>
      <label className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-7 text-center transition-colors ${phase === "uploaded" ? "border-emerald-300 bg-emerald-50" : "border-sky-200 bg-[#F4F9FD] hover:border-sky-400"}`} data-testid="apps-file-drop">
        {phase === "uploaded" ? <CheckCircle2 className="h-7 w-7 text-emerald-600" /> : <UploadCloud className="h-7 w-7 text-sky-600" />}
        <span className="break-words text-sm font-medium text-ink">{file ? `${file.name} · ${fmtSize(file.size)}` : pkg ? "Choose a package — .aeropack (Seed, Config, Deployment, Return) or any data file · max 900 MB" : `Choose a file — ${fileTypes.length ? fileTypes.map((t) => (t.ext ? `${t.name} (.${t.ext})` : t.name)).join(", ") : "any type"} · max 900 MB`}</span>
        {phase === "uploading" && <UploadStats progress={progress} stats={stats} />}
        {phase === "uploaded" && <span className="text-xs font-medium text-emerald-700" data-testid="apps-upload-done">Uploaded — now add the details and save</span>}
        <input ref={inputRef} type="file" className="sr-only" disabled={phase === "saving"} onChange={(e) => pick(e.target.files?.[0] ?? null)} data-testid="apps-file-input" />
      </label>
      {apk && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-2xl bg-[#F4F9FD] p-4 text-xs" data-testid="apps-apk-info">
          {[["Package", apk.package_name], ["Version", apk.version_name || "—"], ["Version code", apk.version_code ?? "—"], ["SDK min / target", `${apk.min_sdk || "?"} / ${apk.target_sdk || "?"}`]].map(([k, v]) => (
            <div key={k} className="min-w-0"><p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{k}</p><p className="break-words font-medium text-ink">{v}</p></div>
          ))}
        </div>
      )}
      {pack?.info && <PackageDetails info={pack.info} check={pack.check} />}
      {bundleBuild != null && <p className="mt-3 rounded-2xl bg-[#F4F9FD] p-4 text-xs" data-testid="apps-bundle-build"><span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Build number</span><span className="block font-medium text-ink">{bundleBuild}</span></p>}
      <BundleContents entries={bundle.entries} loading={bundle.loading} ruleCount={requiredPatterns.length}
        missing={bundle.entries ? findMissing(requiredPatterns, bundle) : []} />
      {apkNote && <p className="mt-3 flex gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" role="status" data-testid="apps-apk-note"><AlertCircle className="h-4 w-4 shrink-0" />{apkNote}</p>}
      {phase === "uploading" && (
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-sky-100"><div className="h-full rounded-full bg-sky-600 transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
          <button type="button" onClick={stopUpload} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50" data-testid="apps-upload-cancel"><Square className="h-3 w-3 fill-red-600" />Stop upload</button>
        </div>
      )}
      {phase === "uploaded" && (
        <button type="button" onClick={stopUpload} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-red-700" data-testid="apps-upload-discard"><X className="h-3.5 w-3.5" />Discard this upload</button>
      )}
      <p className={`${label} mt-6`}>Step 2 · Details &amp; save</p>
      {pkg ? (
        <UnitPicker units={pkg.units} info={pack?.info} value={unitIds} onChange={setUnitIds} newUnit={newUnit} />
      ) : (
      <label className="mb-3 block"><span className={label}>Tab</span>
        <select required value={tabId} onChange={(e) => setTabId(e.target.value)} className={inputCls} data-testid="apps-tab-select">
          {tabs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select></label>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block"><span className={label}>Date</span>
          <input type="date" required value={meta.fileDate} onChange={(e) => setMeta({ ...meta, fileDate: e.target.value })} className={inputCls} data-testid="apps-date-input" /></label>
        <label className="block"><span className={label}>Version</span>
          <input required maxLength={50} placeholder="1.0.0" value={meta.version} onChange={(e) => setMeta({ ...meta, version: e.target.value })} className={inputCls} data-testid="apps-version-input" /></label>
        <label className="block"><span className={`${label} flex items-center justify-between`}>Type
            <button type="button" onClick={() => setEditTypes((v) => !v)} className="normal-case tracking-normal text-sky-700 hover:underline" data-testid="apps-types-edit-toggle">{editTypes ? "Close" : "Edit list"}</button></span>
          <input required maxLength={50} list="apps-type-options" value={meta.fileType} onChange={(e) => setMeta({ ...meta, fileType: e.target.value })} className={inputCls} data-testid="apps-type-input" />
          <datalist id="apps-type-options">{fileTypes.map((t) => <option key={t.name} value={t.name} />)}</datalist></label>
      </div>
      {editTypes && <FileTypesEditor types={fileTypes} onSaved={onTypesSaved} onClose={() => setEditTypes(false)} />}
      <textarea required rows={3} maxLength={2000} placeholder="Description — what's new, what to test" value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className={`${inputCls} mt-3 resize-none`} data-testid="apps-description-input" />
      <label className="mt-3 block"><span className={label}>SHA-256 from build job · optional</span>
        <input maxLength={400} spellCheck={false} placeholder="Paste the checksum to verify the file after it's saved" value={meta.sha} onChange={(e) => setMeta({ ...meta, sha: e.target.value })} className={`${inputCls} font-mono text-xs`} data-testid="apps-sha-input" />
        <span className="mt-1 block text-xs text-slate-600" data-testid="apps-sha-hint">
          {!meta.sha.trim() ? "The portal always works out the file's SHA-256 and shows it to testers. Add the build job's checksum to check they match." : pickSha(meta.sha) ? `Will check against ${pickSha(meta.sha).slice(0, 12)}…${pickSha(meta.sha).slice(-6)}. A mismatch gives a warning but the file is still saved.` : "Not a SHA-256 checksum yet (needs 64 characters, 0–9 and a–f)."}
        </span></label>
      {error && (
        <div className="mt-4 flex gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert" data-testid="apps-upload-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError("")} aria-label="Dismiss error" className="text-red-700 hover:text-red-900" data-testid="apps-upload-error-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}
      <button type="submit" disabled={phase !== "uploaded"} className={`${btnDark} mt-4 w-full sm:w-auto`} data-testid="apps-upload-submit">
        {phase === "saving" ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving</> : "Save & create link"}
      </button>
    </form>
  );
};
